import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { UserPlus, FolderGit2, Check, X, ShieldCheck } from 'lucide-react';
import './AdminPages.css';

const AdminAssignments = () => {
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const { addToast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [empRes, projRes, assignRes] = await Promise.all([
        api.get('/employees/'),
        api.get('/projects/'),
        api.get('/project-assignments/')
      ]);
      setEmployees(empRes.data.results || empRes.data);
      setProjects(projRes.data.results || projRes.data);
      setAssignments(assignRes.data.results || assignRes.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load project assignments matrix.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isAssigned = (empId, projId) => {
    return assignments.some(a => a.employee === empId && a.project === projId && a.is_active);
  };

  const toggleAssignment = async (empId, projId) => {
    const existing = assignments.find(a => a.employee === empId && a.project === projId);

    try {
      if (existing) {
        if (existing.is_active) {
          // Deactivate or delete
          await api.delete(`/project-assignments/${existing.id}/`);
          addToast('Project unassigned from employee.', 'info');
        }
      } else {
        await api.post('/project-assignments/', {
          employee: empId,
          project: projId,
          is_active: true
        });
        addToast('Project assigned successfully!', 'success');
      }
      fetchData();
    } catch (err) {
      console.error(err);
      addToast('Failed to update assignment.', 'error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar />
        <main style={{ flex: 1, padding: '32px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          
          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 800 }}>Project Assignment Matrix</h1>
            <p style={{ color: '#64748b' }}>Assign multiple projects to employees to grant repository access and AI mentor privileges.</p>
          </div>

          {loading ? <LoadingSpinner label="Loading assignment matrix..." /> : (
            <div className="admin-table-card">
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      {projects.map(p => (
                        <th key={p.id} style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 700, color: '#f8fafc' }}>{p.name}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id}>
                        <td style={{ fontWeight: 600 }}>
                          <div>{emp.user?.first_name ? `${emp.user.first_name} ${emp.user.last_name || ''}` : emp.user?.username}</div>
                          <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 400 }}>@{emp.user?.username}</div>
                        </td>
                        <td>
                          <span className="badge badge-info">{emp.department_detail?.name || 'Unassigned'}</span>
                        </td>
                      {projects.map(proj => {
                        const assigned = isAssigned(emp.id, proj.id);
                        return (
                          <td key={proj.id} style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <button
                              onClick={() => toggleAssignment(emp.id, proj.id)}
                              className={`btn btn-sm ${assigned ? 'btn-primary' : 'btn-secondary'}`}
                              style={{ padding: '6px 12px', minWidth: '90px' }}
                            >
                              {assigned ? (
                                <> <Check size={14} /> Assigned </>
                              ) : (
                                <> Assign </>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        </main>
      </div>
    </div>
  );
};

export default AdminAssignments;
